'use client';

import { useState, useEffect } from 'react';
import { useEditorsPicks } from '@/lib/hooks/useSiteData';
import type { BlogPost } from '@/types/blog';

const EditorsPicks = () => {
  const { data: blogResponse, loading, error } = useEditorsPicks();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
    console.log(blogResponse?.data?.filter((post: BlogPost) => post.isActive));
    
  // Transform API data to match component needs
  const editorPicks = blogResponse?.data?.filter((post: BlogPost) => post.isActive).map((post: BlogPost) => {
    // Extract image and summary from specifications
    const imageSpec = post.specifications?.find(spec => spec.key === 'image');
    const summarySpec = post.specifications?.find(spec => spec.key === 'summary');
    
    return {
      id: post.id,
      image: imageSpec?.value || '/images/boxes/card_1.jpg', // fallback image
      title: post.title,
      excerpt: summarySpec?.value || 'Read more about this topic...', // fallback text
      link: `/blog/${post.slug}`,
    };
  }) || [];

  // Responsive items per view
  const getItemsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 1; // Mobile
      if (window.innerWidth < 768) return 2; // Tablet
      return 3; // Desktop
    }
    return 3;
  };

  // Update items per view on resize
  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };

    if (typeof window !== 'undefined') {
      setItemsPerView(getItemsPerView());
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  const nextSlide = () => {
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, editorPicks.length - itemsPerView);
      const next = prev + 1;
      return next > maxIndex ? 0 : next;
    });
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => {
      const maxIndex = Math.max(0, editorPicks.length - itemsPerView);
      const prevIndex = prev - 1;
      return prevIndex < 0 ? maxIndex : prevIndex;
    });
  };

  // Show loading state
  if (loading) {
    return (
      <section className="eiditor-sec bdm">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="eiditor-heading">
                <h2>Editor's Picks</h2>
              </div>
            </div>
            <div className="col-12">
              <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show error state
  if (error) {
    return (
      <section className="eiditor-sec bdm">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="eiditor-heading">
                <h2>Editor's Picks</h2>
              </div>
            </div>
            <div className="col-12">
              <div className="alert alert-warning" role="alert">
                Unable to load Editor's Picks. Please try again later.
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Show empty state
  if (!editorPicks.length) {
    return (
      <section className="eiditor-sec bdm">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="eiditor-heading">
                <h2>Editor's Picks</h2>
              </div>
            </div>
            <div className="col-12">
              <div className="alert alert-info" role="alert">
                No articles available at the moment.
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="eiditor-sec bdm">
      <div className="container">
        <div className="row">
          <div className="col-lg-12">
            <div className="eiditor-heading">
              <h2>Editor's Picks</h2>
            </div>
          </div>
          <div className="col-12">
            <div className="position-relative">
              {/* Navigation Arrow - Only shown on desktop */}
              {itemsPerView === 3 && (
                <button
                  onClick={prevSlide}
                  className="position-absolute top-50 translate-middle-y border-0 d-flex align-items-center justify-content-center"
                  style={{
                    left: '-100px',
                    width: '59px',
                    height: '59px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--primary)',
                    zIndex: 10,
                    transition: 'all 0.3s ease-in-out'
                  }}
                  aria-label="Previous"
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--primary)';
                  }}
                >
                  <i className="bi bi-chevron-left" style={{ fontSize: '24px', color: 'var(--white)' }}></i>
                </button>
              )}

              {/* Carousel Container */}
              <div
                style={{
                  overflow: 'visible',
                  position: 'relative',
                  marginLeft: itemsPerView === 3 ? '100px' : 0
                }}
              >
                <div 
                  className="d-flex"
                  style={{ 
                    transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
                    transition: 'transform 0.5s ease-in-out'
                  }}
                >
                  {editorPicks.map((pick) => (
                    <div 
                      key={pick.id}
                      className="flex-shrink-0 px-2"
                      style={{ width: `${100 / itemsPerView}%` }}
                    >
                      <div className="eiditor-card h-100">
                        <div className="eiditor-img">
                          <img src={pick.image} alt="" />
                        </div>
                        <div className="deatail">
                          <a href={pick.link}>
                            <h4>{pick.title}</h4>
                            <p>{pick.excerpt}</p>
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Dots for mobile/tablet */}
              {itemsPerView < 3 && (
                <div className="d-flex justify-content-center gap-2 mt-4">
                  {Array.from({ length: Math.ceil(editorPicks.length / itemsPerView) }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className="border-0 p-0"
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: currentIndex === index ? '#F9960D' : '#ccc',
                        opacity: currentIndex === index ? 1 : 0.5,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EditorsPicks;
